import { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { 
    CheckCircleOutlined, 
    ErrorOutlined, 
    NotificationsOutlined, 
    PendingOutlined, 
    RefreshRounded,
    Add,
    Sync
} from '@mui/icons-material';
import PulsingIcon, { pulse } from '../components/common/PulsingIcon';
import { useIntegrationsStatusContext } from '../context/IntegrationsStatusContext';
import { useRecalculation } from '../context/RecalculationStatusContext';
import { useTransactionService, DetailsType } from '../services/transaction.service';
import { useIgnoredIssues } from '../components/smart-check/hooks/useIgnoredIssues';
import { QUERY_KEYS } from '../utils/queryKeys';
import { useAuth } from '../context/AuthContext';

export interface UnifiedAccountState {
    // Status bar info (from existing StatusIndicator)
    status: string;
    icon: JSX.Element;
    color: string;
    details: string;
    
    // State classification
    stateType: 'processing' | 'error' | 'warning' | 'success';
    priority: number; // Lower number = higher priority
    
    // Guidance info for pages
    guidance?: {
        message: string;
        actionText?: string;
        actionRoute?: string;
        variant: 'success' | 'warning' | 'error' | 'info';
    };
    
    // Navigation target (where to go when status is clicked)
    navigationTarget?: string;
    
    // Additional context
    hasIntegrations: boolean;
    hasRecentSync: boolean;
    hasIssues: boolean;
    isProcessing: boolean;
}

interface UseUnifiedAccountStateOptions {
    issuesData?: any; // Optional override for issue data
    ignoredIssues?: any; // Optional override for ignored issues
    iconSize?: string;
    skipIssueFetch?: boolean; // Skip fetching issues (for performance)
}

// Helper function to calculate total issue count (from StatusIndicator)
const calculateIssueCount = (data: any): number => {
    if (!data) return 0;
    
    let count = 0;
    if (typeof data === 'object' && !Array.isArray(data)) {
        count += (data.missingCostBasisTransactions || []).length;
        count += (data.missingPriceIncomeTransactions || []).length;
    } else if (Array.isArray(data)) {
        count = data.length;
    }
    return count;
};

// Helper function to calculate active ignored count (from StatusIndicator)
const calculateActiveIgnoredCount = (issuesData: any, ignoredIssues: any): number => {
    if (!issuesData || !ignoredIssues) return 0;
    
    // Get all current issue IDs from the issues data
    const currentIssueIds = new Set<string>();
    
    if (typeof issuesData === 'object' && !Array.isArray(issuesData)) {
        // Add cost basis issue IDs
        (issuesData.missingCostBasisTransactions || []).forEach((issue: any) => {
            currentIssueIds.add(issue.id?.toString());
        });
        // Add missing price issue IDs  
        (issuesData.missingPriceIncomeTransactions || []).forEach((issue: any) => {
            currentIssueIds.add(issue.id?.toString());
        });
    } else if (Array.isArray(issuesData)) {
        issuesData.forEach((issue: any) => {
            currentIssueIds.add(issue.id?.toString());
        });
    }
    
    // Count ignored issues that still exist in current results
    return Object.keys(ignoredIssues).filter(ignoredIssueId => 
        currentIssueIds.has(ignoredIssueId)
    ).length;
};

export const useUnifiedAccountState = ({
    issuesData: overrideIssuesData,
    ignoredIssues: overrideIgnoredIssues,
    iconSize = '1.5rem',
    skipIssueFetch = false
}: UseUnifiedAccountStateOptions = {}): UnifiedAccountState => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentCtUser } = useAuth();
    const transactionService = useTransactionService();
    const { status: recalculationStatus } = useRecalculation();
    const { inProgressIntegrations, integrations, isLoading } = useIntegrationsStatusContext();
    
    // Fetch issues data if not provided and not skipped
    const { data: fetchedIssuesData } = useQuery({
        queryKey: [QUERY_KEYS.ALL_TIME_ISSUES],
        queryFn: () => transactionService.getAllTimeIssueCount(DetailsType.ONLY_ISSUES),
        enabled: !!currentCtUser && !skipIssueFetch && !overrideIssuesData,
    });
    
    // Fetch ignored issues if not provided
    const { data: fetchedIgnoredIssues } = useIgnoredIssues();
    
    // Use override data if provided, otherwise use fetched data
    const issuesData = overrideIssuesData ?? fetchedIssuesData;
    const ignoredIssues = overrideIgnoredIssues ?? fetchedIgnoredIssues;

    // Calculate the raw state
    const rawState = useMemo(() => {
        // Priority 0: Initial loading state (when we don't have data yet)
        if (isLoading && integrations.length === 0) {
            return {
                status: t('sidebar.status.loadingData'),
                icon: <PulsingIcon IconComponent={RefreshRounded} size={iconSize} />,
                color: theme.palette.info.main,
                details: t('sidebar.status.loadingData'),
                stateType: 'processing',
                priority: 0,
                isProcessing: true,
                hasIntegrations: false,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: t('sidebar.status.loadingData'),
                    variant: 'info'
                }
            };
        }

        // Priority 1: Processing states (highest priority)
        if (recalculationStatus?.run || recalculationStatus?.new) {
            return {
                status: t('sidebar.status.recalculatingBalances'),
                icon: <PulsingIcon IconComponent={PendingOutlined} size={iconSize} />,
                color: theme.palette.info.main,
                details: t('sidebar.status.recalculatingBalances'),
                stateType: 'processing',
                priority: 1,
                isProcessing: true,
                hasIntegrations: integrations && integrations.length > 0,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: t('sidebar.status.recalculatingBalances'),
                    variant: 'info'
                }
            };
        }

        if (inProgressIntegrations.length > 0) {
            // All in-progress integrations show as syncing
            const inProgressCount = inProgressIntegrations.length;
            const syncMessage = inProgressCount > 1
                ? t('sidebar.status.syncingMultiple', { count: inProgressCount })
                : t('sidebar.status.currentlySyncing');
                
            return {
                status: syncMessage,
                icon: (
                    <RefreshRounded sx={{ 
                        fontSize: iconSize, 
                        animation: `${pulse} 2s infinite ease-in-out, spin 2s linear infinite`,
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        },
                    }} />
                ),
                color: theme.palette.info.main,
                details: syncMessage,
                stateType: 'processing',
                priority: 2,
                isProcessing: true,
                hasIntegrations: true,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: syncMessage,
                    variant: 'info'
                }
            };
        }

        // Priority 2: Integration errors
        const errorIntegrations = integrations?.filter(integration => 
            integration.status.status === 'error'
        ) || [];

        if (errorIntegrations.length > 0) {
            const errorMessage = errorIntegrations.length === 1 
                ? t('integrations.guidance.errorSingle')
                : t('integrations.guidance.errorMultiple', { count: errorIntegrations.length });
                
            return {
                status: errorMessage,
                icon: <ErrorOutlined sx={{ fontSize: iconSize }} />,
                color: theme.palette.error.main,
                details: errorMessage,
                stateType: 'error',
                priority: 2,
                navigationTarget: '/integrations',
                isProcessing: false,
                hasIntegrations: true,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: errorMessage,
                    actionText: t('guidance.reviewIssues'),
                    actionRoute: '/integrations',
                    variant: 'error'
                }
            };
        }

        // Check if we have integrations at all
        const hasIntegrations = integrations && integrations.length > 0;
        
        if (!hasIntegrations) {
            return {
                status: t('integrations.overview.welcomeMessage'),
                icon: <Add sx={{ fontSize: iconSize, color: theme.palette.primary.main }} />,
                color: theme.palette.primary.main,
                details: t('integrations.overview.welcomeMessage'),
                stateType: 'warning',
                priority: 3,
                navigationTarget: '/integrations',
                isProcessing: false,
                hasIntegrations: false,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: t('integrations.overview.welcomeMessage'),
                    actionText: t('integrations.overview.addButton'),
                    actionRoute: '/integrations',
                    variant: 'warning'
                }
            };
        }

        // Check for recent sync (within 24 hours)
        // CSV-only integrations are always considered "fresh" since they don't auto-sync
        const hasRecentSync = integrations.some(integration => {
            // Non-API integrations (CSV, manual, blockchain) are always "fresh" since they're manually managed
            if (!integration.importTypes?.includes('api')) {
                return true;
            }
            
            // For API-capable integrations, check the sync time
            if (!integration.endTime) {
                return false;
            }
            
            const thresholdMs = Date.now() - (24 * 60 * 60 * 1000);
            const endTimeMs = integration.endTime * 1000;
            return endTimeMs > thresholdMs;
        });

        // Priority 3: No sync or stale sync
        if (!hasRecentSync) {
            return {
                status: t('guidance.noSync'),
                icon: <Sync sx={{ fontSize: iconSize, color: theme.palette.warning.main }} />,
                color: theme.palette.warning.main,
                details: t('guidance.staleData'),
                stateType: 'warning',
                priority: 3,
                navigationTarget: '/integrations',
                isProcessing: false,
                hasIntegrations: true,
                hasRecentSync: false,
                hasIssues: false,
                guidance: {
                    message: t('guidance.staleData'),
                    actionText: t('guidance.syncNow'),
                    actionRoute: '/integrations',
                    variant: 'warning'
                }
            };
        }

        // Priority 4: Smart Check issues (excluding actively ignored ones)
        const totalIssueCount = calculateIssueCount(issuesData);
        const activeIgnoredCount = calculateActiveIgnoredCount(issuesData, ignoredIssues);
        const netIssueCount = totalIssueCount - activeIgnoredCount;
        
        if (netIssueCount > 0) {
            const issueMessage = t('integrations.guidance.issuesFound', { count: netIssueCount });
            
            return {
                status: t('smartCheck.totalIssuesText', { count: netIssueCount }),
                icon: <NotificationsOutlined sx={{ fontSize: iconSize }} />,
                color: theme.palette.warning.main,
                details: issueMessage,
                stateType: 'warning',
                priority: 4,
                navigationTarget: '/smart-check',
                isProcessing: false,
                hasIntegrations: true,
                hasRecentSync: true,
                hasIssues: true,
                guidance: {
                    message: issueMessage,
                    actionText: t('integrations.guidance.reviewIssues'),
                    actionRoute: '/smart-check',
                    variant: 'warning'
                }
            };
        }

        // Priority 5: All good
        return {
            status: t('sidebar.status.allOk'),
            icon: <CheckCircleOutlined sx={{ fontSize: iconSize }} />,
            color: theme.palette.success.main,
            details: t('sidebar.status.allDataUpToDate'),
            stateType: 'success',
            priority: 5,
            isProcessing: false,
            hasIntegrations: true,
            hasRecentSync: true,
            hasIssues: false,
            guidance: {
                message: t('integrations.guidance.allGood'),
                actionText: t('integrations.guidance.goToTaxReport'),
                actionRoute: '/tax-reports',
                variant: 'success'
            }
        };
    }, [
        recalculationStatus,
        inProgressIntegrations,
        integrations,
        issuesData,
        ignoredIssues,
        theme,
        t,
        iconSize,
        isLoading
    ]);

    // Debounce state changes to prevent rapid flashing
    const [debouncedState, setDebouncedState] = useState<UnifiedAccountState>(rawState);
    const debounceTimerRef = useRef<NodeJS.Timeout>();
    
    useEffect(() => {
        // Clear any existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // For high-priority states (processing), update immediately
        if (rawState.priority <= 2 || debouncedState.priority <= 2) {
            setDebouncedState(rawState);
        } else {
            // For lower priority states, debounce for 500ms
            debounceTimerRef.current = setTimeout(() => {
                setDebouncedState(rawState);
            }, 500);
        }
        
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [rawState]);
    
    return debouncedState;
};